// src/services/aiPipelineService.js
import { pb } from "./pocketbase";

/**
 * Helper para requisições com retry automático (Google / OpenAI / Anthropic).
 * Trata erros de rede, 429 (rate-limit) e 503 (serviço ocupado) com backoff exponencial.
 * Garante que respostas não-200 sejam tratadas com segurança sem estourar "Unexpected token <".
 */
export const fetchWithRetry = async (url, options, maxRetries = 3, initialDelay = 1500, onRetry = null) => {
  let delay = initialDelay;
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      const res = await fetch(url, options);
      if (res.ok) return res;

      // Lê corpo de erro antecipadamente para distinguir rate-limit momentâneo de cota diária esgotada
      let errMsg = `HTTP ${res.status}: ${res.statusText}`;
      try {
        const errJson = await res.clone().json();
        errMsg = errJson.error?.message || errJson.message || JSON.stringify(errJson);
      } catch (_) {
        try {
          const errText = await res.clone().text();
          if (errText) errMsg = errText.substring(0, 250);
        } catch (__) { }
      }

      const lowerMsg = (errMsg || "").toLowerCase();
      const isHardQuotaExhausted =
        lowerMsg.includes("exceeded your current quota") ||
        lowerMsg.includes("per day") ||
        lowerMsg.includes("free_tier") ||
        lowerMsg.includes("credit balance is too low") ||
        lowerMsg.includes("insufficient_quota");

      // Se for cota diária/crédito esgotado, não perde tempo retentando a mesma URL — lança imediato p/ acionar fallback de modelo/provedor
      if (isHardQuotaExhausted) {
        throw new Error(errMsg);
      }

      // Rate limit momentâneo (RPM) ou serviço temporariamente ocupado (503/529)
      if (res.status === 429 || res.status === 503 || res.status === 529) {
        if (attempt < maxRetries - 1) {
          if (onRetry) onRetry(attempt + 1, delay, res.status);
          await new Promise(resolve => setTimeout(resolve, delay));
          delay *= 2;
          continue;
        }
      }

      throw new Error(errMsg);

    } catch (err) {
      if (attempt < maxRetries - 1 && (err.message?.includes("Failed to fetch") || err.message?.includes("NetworkError"))) {
        if (onRetry) onRetry(attempt + 1, delay, "Erro de Conexão");
        await new Promise(resolve => setTimeout(resolve, delay));
        delay *= 2;
        continue;
      }
      throw err;
    }
  }
};

/**
 * Normaliza o modelo para provedores conhecidos, atualizando modelos descontinuados.
 */
export const normalizeModelName = (provider, modelName) => {
  const m = (modelName || "").trim().replace(/^models\//i, "");
  if (provider === "google") {
    if (!m || m === "gemini-1.5-flash" || m === "gemini-1.5-flash-latest" || m === "gemini-1.5-flash-001" || m === "gemini-1.5-flash-002" || m === "gemini-flash") {
      return "gemini-2.5-flash";
    }
    if (m === "gemini-1.5-pro" || m === "gemini-1.5-pro-latest" || m === "gemini-pro") {
      return "gemini-2.5-pro";
    }
    return m;
  }
  if (provider === "openai") {
    return m || "gpt-4o";
  }
  if (provider === "anthropic") {
    if (!m || m === "claude-3-5-sonnet") return "claude-3-5-sonnet-20241022";
    return m;
  }
  return m;
};

/**
 * Executa chamada ao Google Gemini com fallback automático de modelos caso a API v1beta retorne:
 * - 404 (modelo descontinuado/não encontrado)
 * - 429 / Quota Exceeded
 */
export const generateGeminiContentWithFallback = async ({
  apiKey,
  preferredModel,
  promptText,
  temperature = 0.7,
  onStep = null
}) => {
  const candidates = [
    normalizeModelName("google", preferredModel),
    "gemini-2.5-flash",
    "gemini-2.0-flash",
    "gemini-2.5-flash-lite",
    "gemini-2.0-flash-lite",
    "gemini-flash-latest",
    "gemini-2.5-pro"
  ].filter((v, i, a) => v && a.indexOf(v) === i);

  const buildBody = () =>
    JSON.stringify({
      contents: [
        {
          role: "user",
          parts: [{ text: promptText }]
        }
      ],
      generationConfig: {
        temperature
      }
    });

  let lastError = null;

  for (const modelName of candidates) {
    try {
      const url = `https://generativelanguage.googleapis.com/v1beta/models/${modelName}:generateContent?key=${apiKey}`;
      const res = await fetchWithRetry(
        url,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: buildBody()
        },
        2,
        1200,
        (attempt, nextDelay, status) => {
          if (onStep) onStep(`Gemini (${modelName}) ocupado (${status}). Re-tentando (${attempt}/2)...`);
        }
      );

      const resData = await res.json();
      return {
        text: resData.candidates?.[0]?.content?.parts?.[0]?.text || "",
        modelUsed: modelName
      };
    } catch (err) {
      lastError = err;
      const msg = (err?.message || "").toLowerCase();
      const shouldTryNextModel =
        msg.includes("not found") ||
        msg.includes("not supported for generatecontent") ||
        msg.includes("404") ||
        msg.includes("quota") ||
        msg.includes("rate-limit") ||
        msg.includes("rate limit") ||
        msg.includes("resource_exhausted") ||
        msg.includes("429") ||
        msg.includes("overloaded") ||
        msg.includes("503");

      if (shouldTryNextModel) {
        console.warn(`[Gemini Fallback] Modelo '${modelName}' indisponível ou sem cota (${err.message?.substring(0, 80)}). Alternando para próximo modelo...`);
        if (onStep) onStep(`Cota de ${modelName} atingida. Alternando automaticamente para próximo modelo Gemini...`);
        continue;
      }
      throw err;
    }
  }

  // Último recurso: consulta ListModels diretamente na API com a chave do usuário
  try {
    const listRes = await fetch(`https://generativelanguage.googleapis.com/v1beta/models?key=${apiKey}`);
    if (listRes.ok) {
      const listData = await listRes.json();
      const available = (listData.models || [])
        .filter(m => m.supportedGenerationMethods?.includes("generateContent"))
        .map(m => m.name.replace(/^models\//, ""))
        .filter(m => !candidates.includes(m));

      for (const dynamicModel of available.slice(0, 3)) {
        try {
          const url = `https://generativelanguage.googleapis.com/v1beta/models/${dynamicModel}:generateContent?key=${apiKey}`;
          const res = await fetchWithRetry(url, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: buildBody()
          }, 1);
          const resData = await res.json();
          return {
            text: resData.candidates?.[0]?.content?.parts?.[0]?.text || "",
            modelUsed: dynamicModel
          };
        } catch (_) { }
      }
    }
  } catch (_) { }

  throw lastError || new Error("Cota excedida em todos os modelos Gemini disponíveis para esta chave.");
};

/**
 * Executa chamada à Anthropic (Claude) com suporte direto a navegador (CORS header oficial)
 * e fallback automático entre modelos Claude 3.5 / 3.7 / 4 / Haiku.
 */
export const generateAnthropicContentWithFallback = async ({
  apiKey,
  preferredModel,
  systemPrompt,
  userInput,
  temperature = 0.7,
  onStep = null
}) => {
  const candidates = [
    normalizeModelName("anthropic", preferredModel),
    "claude-sonnet-4-20250514",
    "claude-3-7-sonnet-latest",
    "claude-3-5-sonnet-latest",
    "claude-3-5-sonnet-20241022",
    "claude-3-5-haiku-latest",
    "claude-3-haiku-20240307"
  ].filter((v, i, a) => v && a.indexOf(v) === i);

  let lastError = null;

  for (const modelName of candidates) {
    try {
      const url = "https://api.anthropic.com/v1/messages";
      const res = await fetchWithRetry(
        url,
        {
          method: "POST",
          headers: {
            "x-api-key": apiKey,
            "anthropic-version": "2023-06-01",
            "anthropic-dangerous-direct-browser-access": "true",
            "content-type": "application/json"
          },
          body: JSON.stringify({
            model: modelName,
            max_tokens: 2500,
            temperature,
            system: systemPrompt,
            messages: [{ role: "user", content: userInput }]
          })
        },
        2,
        1500,
        (attempt, nextDelay, status) => {
          if (onStep) onStep(`Claude (${modelName}) ocupado (${status}). Re-tentando (${attempt}/2)...`);
        }
      );

      const resData = await res.json();
      return {
        text: resData.content?.[0]?.text || "",
        modelUsed: modelName
      };
    } catch (err) {
      lastError = err;
      const msg = (err?.message || "").toLowerCase();
      const isModelIssue =
        msg.includes("not_found") ||
        msg.includes("not found") ||
        msg.includes("model") ||
        msg.includes("404") ||
        msg.includes("overloaded") ||
        msg.includes("529");

      if (isModelIssue) {
        console.warn(`[Anthropic Fallback] Modelo '${modelName}' indisponível. Tentando próximo modelo Claude...`);
        continue;
      }
      throw err;
    }
  }

  throw lastError || new Error("Não foi possível gerar conteúdo com a chave da Anthropic configurada.");
};

/**
 * Parser resiliente para JSON gerado por IA.
 * Trata:
 * 1. Blocos de código markdown (```json ... ``` ou ``` ... ```)
 * 2. Textos pré ou pós-bloco (preâmbulos, saudações ou explicações)
 * 3. Vírgulas residuais antes de chaves de fechamento
 * 4. Aspas ou quebras de linha em descrições HTML
 * 5. Fallback gracioso com extração via regex se JSON.parse falhar totalmente.
 */
export const extractAndParseJson = (rawText, fallbackContext = {}) => {
  if (!rawText || typeof rawText !== "string") {
    return createFallbackObject(rawText || "", fallbackContext);
  }

  const trimmed = rawText.trim();

  // 1. Tenta extrair o bloco interno de ```json ... ```
  let candidate = trimmed;
  const jsonBlockRegex = /```(?:json)?\s*([\s\S]*?)\s*```/i;
  const matchBlock = trimmed.match(jsonBlockRegex);
  if (matchBlock && matchBlock[1]) {
    candidate = matchBlock[1].trim();
  }

  // 2. Extrai do primeiro '{' até o último '}'
  const firstBrace = candidate.indexOf("{");
  const lastBrace = candidate.lastIndexOf("}");
  if (firstBrace !== -1 && lastBrace !== -1 && lastBrace > firstBrace) {
    candidate = candidate.substring(firstBrace, lastBrace + 1).trim();
  }

  // 3. Primeira tentativa direta de JSON.parse
  try {
    const parsed = JSON.parse(candidate);
    if (parsed && typeof parsed === "object") {
      return normalizeResultFields(parsed, fallbackContext, rawText);
    }
  } catch (err1) {
    // Continua para sanitização
  }

  // 4. Sanitização de vírgulas sobrando antes de '}' ou ']'
  try {
    const withoutTrailingCommas = candidate.replace(/,\s*([}\]])/g, "$1");
    const parsed = JSON.parse(withoutTrailingCommas);
    if (parsed && typeof parsed === "object") {
      return normalizeResultFields(parsed, fallbackContext, rawText);
    }
  } catch (_) {
    // Continua
  }

  // 5. Extração resiliente via Expressões Regulares para cada campo-chave
  const regexExtractField = (keys) => {
    for (const k of keys) {
      // Procura "campo": "valor..."
      const pattern = new RegExp(`"${k}"\\s*:\\s*"((?:\\\\.|[^"\\\\])*)"`, "i");
      const m = candidate.match(pattern);
      if (m && m[1]) {
        return m[1].replace(/\\n/g, "\n").replace(/\\"/g, '"');
      }
    }
    return "";
  };

  const regexPageTitle = regexExtractField(["page_title", "nome_produto", "meta_title", "titulo"]);
  const regexDescSmall = regexExtractField(["description_small", "descricao_breve", "chamada"]);
  const regexPageDesc = regexExtractField(["page_description", "meta_description", "seo_description"]);
  const regexDescFull = regexExtractField(["description_full", "descricao_completa", "html", "conteudo"]);

  // Procura keywords (string ou array)
  let regexKeywords = "";
  const kwArrayPattern = /"(?:page_keywords|palavras_chave)"\s*:\s*\[([\s\S]*?)\]/i;
  const kwMatch = candidate.match(kwArrayPattern);
  if (kwMatch && kwMatch[1]) {
    regexKeywords = kwMatch[1]
      .split(",")
      .map(s => s.replace(/["'\s]/g, "").trim())
      .filter(Boolean)
      .join(", ");
  } else {
    regexKeywords = regexExtractField(["page_keywords", "palavras_chave", "tags"]);
  }

  if (regexPageTitle || regexDescFull || regexDescSmall) {
    const parsedFromRegex = {
      page_title: regexPageTitle,
      description_small: regexDescSmall,
      page_description: regexPageDesc,
      page_keywords: regexKeywords,
      description_full: regexDescFull || rawText
    };
    return normalizeResultFields(parsedFromRegex, fallbackContext, rawText);
  }

  // 6. Fallback final seguro (converte texto livre em HTML amigável)
  return createFallbackObject(rawText, fallbackContext);
};

/**
 * Cria um objeto de saída válido mesmo se a IA tiver retornado apenas texto livre.
 */
export const createFallbackObject = (rawText, context = {}) => {
  const nomeBase = context.nome || (context.sku ? `Joia ${context.sku}` : "Joia Exclusiva Sempre Joias");
  const cleanSku = context.sku || "";
  const cat = context.categoria || "Joias";

  // Transforma texto livre em parágrafos HTML limpos
  const safeHtml = rawText
    ? `<p>${rawText.trim().replace(/\n\s*\n/g, "</p><p>").replace(/\n/g, "<br/>")}</p>`
    : `<p>Confeccionada com acabamento impecável em metal nobre, perfeita para momentos especiais.</p>`;

  const cleanSummary = (rawText || nomeBase)
    .replace(/<[^>]+>/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .substring(0, 150);

  return normalizeResultFields({
    page_title: `${nomeBase}${cleanSku ? ` - ${cleanSku}` : ""}`,
    description_small: cleanSummary,
    page_description: cleanSummary.substring(0, 155),
    page_keywords: `${cat.toLowerCase()}, joia fina, sempre joias, ouro, prata${cleanSku ? `, ${cleanSku}` : ""}`,
    description_full: safeHtml
  }, context, rawText);
};

/**
 * Normaliza os campos para que funcionem TANTO na Ficha do Produto quanto no Gerador de Anúncios.
 */
export const normalizeResultFields = (parsed, context = {}, rawText = "") => {
  const p = parsed || {};

  const pageTitle = (p.page_title || p.nome_produto || p.meta_title || p.titulo || context.nome || "Joia Sempre Joias").trim();
  const descSmall = (p.description_small || p.descricao_breve || p.resumo || "").trim();
  const pageDesc = (p.page_description || p.meta_description || descSmall || "").trim();

  // Palavras-chave: array e string
  let kwString = "";
  let kwArray = [];
  if (Array.isArray(p.palavras_chave)) {
    kwArray = p.palavras_chave.map(k => String(k).trim()).filter(Boolean);
    kwString = kwArray.join(", ");
  } else if (Array.isArray(p.page_keywords)) {
    kwArray = p.page_keywords.map(k => String(k).trim()).filter(Boolean);
    kwString = kwArray.join(", ");
  } else if (typeof p.page_keywords === "string") {
    kwString = p.page_keywords.trim();
    kwArray = kwString.split(",").map(k => k.trim()).filter(Boolean);
  } else if (typeof p.palavras_chave === "string") {
    kwString = p.palavras_chave.trim();
    kwArray = kwString.split(",").map(k => k.trim()).filter(Boolean);
  } else {
    kwString = `${(context.categoria || "joia").toLowerCase()}, sempre joias, design exclusivo`;
    kwArray = kwString.split(",").map(k => k.trim());
  }

  // Descrição completa HTML
  let descFull = p.description_full || p.descricao_completa || p.html || rawText || "";
  if (!descFull.includes("<") && descFull) {
    descFull = `<p>${descFull.replace(/\n\s*\n/g, "</p><p>").replace(/\n/g, "<br/>")}</p>`;
  }

  return {
    // Campos canônicos do e-commerce / Catálogo (Ficha do Produto)
    page_title: pageTitle,
    description_small: descSmall || descFull.replace(/<[^>]+>/g, "").substring(0, 150),
    page_description: (pageDesc || descSmall || descFull.replace(/<[^>]+>/g, "")).substring(0, 160),
    page_keywords: kwString,
    description_full: descFull,

    // Aliases para o Gerador de Anúncios (retrocompatibilidade perfeita)
    nome_produto: pageTitle,
    descricao_breve: descSmall || descFull.replace(/<[^>]+>/g, "").substring(0, 150),
    palavras_chave: kwArray,
    meta_title: pageTitle,
    meta_description: (pageDesc || descSmall || descFull.replace(/<[^>]+>/g, "")).substring(0, 160),
    descricao_completa: descFull
  };
};

/**
 * Detecta se um prompt foi criado com intenção de ser um Tipo de Anúncio / Canal (2º passo),
 * mesmo que o usuário tenha esquecido o seletor "tipo" no padrão "categoria" ou "modelo".
 */
export const isLikelyAdTypePrompt = (p) => {
  if (!p) return false;
  if (p.tipo === "tipo_anuncio") return true;
  const textToCheck = `${p.contexto || ""} ${p.nome || ""}`.toLowerCase();
  return /mercado\s*livre|\bmeli\b|shopee|instagram|whatsapp|tiktok|magalu|amazon|an[uú]ncio|normalizador|canal|marketplace|\bsite\b|e-commerce|ecommerce/i.test(textToCheck);
};

/**
 * SERVIÇO PRINCIPAL: Executa o Pipeline de IA Unificado
 * 
 * Funciona de forma idêntica tanto para o Gerador de Anúncios quanto para a Ficha do Produto.
 */
export const aiPipelineService = {
  /**
   * Executa o pipeline de IA com base nas tabelas ia_configs e ia_prompts
   */
  async runProductPipeline({
    sku,
    nome,
    categoria = "Anel",
    modelo = "",
    descricaoTecnica = "",
    tipoAnuncio = "Site",
    categoriaPromptId = "",
    tipoAnuncioPromptId = "",
    preferredProviderId = "",
    onStep = null
  }) {
    if (!sku) throw new Error("SKU do produto é obrigatório para rodar o pipeline.");
    if (!nome) throw new Error("Nome do produto é obrigatório.");

    const stepNotify = (msg) => {
      if (onStep) onStep(msg);
    };

    stepNotify("Buscando credenciais dos provedores de IA...");

    // 1. Obtém todos os provedores em ia_configs que possuem chave cadastrada
    let allConfigRecords = [];
    try {
      const configsList = await pb.collection("ia_configs").getFullList({
        sort: "-ativo,nome_exibicao"
      });
      for (const cfg of configsList) {
        try {
          const fullRec = cfg.api_key_encrypted ? cfg : await pb.collection("ia_configs").getOne(cfg.id);
          if (fullRec && fullRec.api_key_encrypted) {
            allConfigRecords.push(fullRec);
          }
        } catch (_) {
          if (cfg.api_key_encrypted) allConfigRecords.push(cfg);
        }
      }
    } catch (e) {
      console.warn("Erro ao buscar provedores de IA:", e);
    }

    const activeConfigRecord =
      allConfigRecords.find(c => c.ativo) || allConfigRecords[0] || null;

    if (!activeConfigRecord) {
      throw new Error(
        "Nenhum provedor de IA com chave de API configurada. Acesse Configurações > Configurações de IA e insira sua chave da Anthropic (Claude), Google Gemini ou OpenAI."
      );
    }

    stepNotify("Carregando agentes e instruções do pipeline...");

    // 2. Obtém os prompts cadastrados
    let allPrompts = [];
    try {
      const records = await pb.collection("ia_prompts").getFullList({
        sort: "ordem"
      });
      allPrompts = (records || []).filter(p => p.ativo !== false);
    } catch (e) {
      console.warn("Erro ao carregar ia_prompts:", e);
    }

    // 3. Monta a lista de agentes do pipeline (Passo 1: Redator Categoria; Passo 2: Normalizador Canal)
    const pipelineAgents = [];

    // Redator de Categoria
    const catLower = (categoria || "").toLowerCase().trim();
    let categoryAgent = null;

    if (categoriaPromptId) {
      categoryAgent = allPrompts.find(p => p.id === categoriaPromptId);
    }
    if (!categoryAgent && catLower) {
      categoryAgent = allPrompts.find(
        p => p.tipo === "categoria" && !isLikelyAdTypePrompt(p) && (
          (p.contexto || "").toLowerCase().trim() === catLower ||
          (p.nome || "").toLowerCase().trim() === catLower
        )
      );
    }
    if (!categoryAgent && catLower) {
      categoryAgent = allPrompts.find(
        p => p.tipo === "categoria" && (p.contexto || "").toLowerCase().trim() === catLower
      );
    }

    // Se não encontrou exato para a categoria, tenta o primeiro de categoria ativo (que não seja canal)
    if (!categoryAgent) {
      categoryAgent = allPrompts.find(p => p.tipo === "categoria" && !isLikelyAdTypePrompt(p));
    }

    // Fallback gracioso se não houver nenhum prompt de categoria no banco
    if (!categoryAgent) {
      categoryAgent = {
        id: "fallback_cat",
        nome: `Redator de Joias (${categoria})`,
        tipo: "categoria",
        contexto: categoria,
        temperatura: 0.7,
        system_prompt: `Você é o Redator Sênior da Sempre Joias. Seu papel é transformar as especificações de joias da categoria "${categoria}" em uma descrição comercial envolvente, sofisticada e altamente persuasiva. Destaque o acabamento de bancada, a nobreza dos metais e o significado da peça para o cliente.`
      };
    }
    pipelineAgents.push(categoryAgent);

    // Normalizador de Canal (ex: Site, Instagram, Mercado Livre)
    const tipoLower = (tipoAnuncio || "site").toLowerCase().trim();
    let normalizerAgent = null;

    if (tipoAnuncioPromptId) {
      normalizerAgent = allPrompts.find(p => p.id === tipoAnuncioPromptId);
    }
    if (!normalizerAgent && tipoLower) {
      normalizerAgent = allPrompts.find(
        p => isLikelyAdTypePrompt(p) && (
          (p.contexto || "").toLowerCase().trim() === tipoLower ||
          (p.nome || "").toLowerCase().trim() === tipoLower
        )
      );
    }

    if (!normalizerAgent) {
      normalizerAgent = allPrompts.find(p => p.tipo === "tipo_anuncio");
    }

    if (!normalizerAgent) {
      normalizerAgent = {
        id: "fallback_norm",
        nome: `Normalizador para ${tipoAnuncio}`,
        tipo: "tipo_anuncio",
        contexto: tipoAnuncio,
        temperatura: 0.2,
        system_prompt: `Você é o Normalizador de Dados do catálogo da Sempre Joias para o canal "${tipoAnuncio}". Transforme o texto recebido em um JSON estruturado estrito com as seguintes chaves:
{
  "page_title": "Título de página otimizado para ${tipoAnuncio} (SEO) com até 60 caracteres",
  "description_small": "Resumo comercial rápido e cativante em até 150 caracteres",
  "page_description": "Meta descrição para busca em até 160 caracteres",
  "page_keywords": "Palavras-chave SEO separadas por vírgula",
  "description_full": "Código HTML limpo e elegante com tags <p>, <strong>, <ul>, <li> descrevendo todos os detalhes da peça"
}
Retorne exclusivamente o JSON, sem nenhuma saudação ou comentário.`
      };
    }

    if (normalizerAgent.id !== categoryAgent.id) {
      pipelineAgents.push(normalizerAgent);
    }

    // 4. Executa a cadeia de agentes com failover automático entre modelos e provedores (ex: Google Gemini ➔ Anthropic Claude)
    let currentInput = `SKU: ${sku}\nProduto: ${nome}\nCategoria: ${categoria}\nCanal / Tipo de Anúncio: ${tipoAnuncio}\nModelo: ${modelo || "N/A"}\nEspecificações Técnicas:\n${descricaoTecnica || "Joia em Prata 925 / Ouro com acabamento refinado."}`;
    const rawOutputs = [];
    let lastUsedProviderName = activeConfigRecord.nome_exibicao || activeConfigRecord.provedor;
    let lastUsedModelName = activeConfigRecord.modelo_padrao || "Padrão";

    for (let i = 0; i < pipelineAgents.length; i++) {
      const agent = pipelineAgents[i];
      const isLastStep = i === pipelineAgents.length - 1;

      stepNotify(`Executando agente ${i + 1}/${pipelineAgents.length}: ${agent.nome}...`);

      const temp = agent.temperatura !== undefined ? Number(agent.temperatura) : 0.7;

      let promptToSend = agent.system_prompt;
      // No último passo (normalizador), garantimos que a IA retorne os campos JSON estruturados mesmo em prompts customizados
      if (isLastStep) {
        const hasSchemaKeys = /nome_produto|page_title|descricao_completa|description_full/i.test(promptToSend);
        if (!hasSchemaKeys) {
          promptToSend += `\n\nIMPORTANTE: Siga rigorosamente todas as instruções acima para o canal "${tipoAnuncio}" e retorne a resposta EXCLUSIVAMENTE em um objeto JSON válido com estas chaves exatas:\n{\n  "nome_produto": "Título comercial otimizado para ${tipoAnuncio} (até 60 caracteres)",\n  "descricao_breve": "Resumo ou chamada principal (até 150 caracteres)",\n  "descricao_completa": "Descrição completa do anúncio para ${tipoAnuncio} (use tags HTML simples como <p>, <strong>, <ul>, <li> para organizar parágrafos e tópicos)",\n  "palavras_chave": ["tag1", "tag2", "tag3", "tag4", "tag5"],\n  "meta_title": "Título SEO (até 60 caracteres)",\n  "meta_description": "Meta descrição SEO (até 160 caracteres)"\n}\nResponda apenas o JSON puro, sem textos adicionais.`;
        } else {
          promptToSend += `\n\nATENÇÃO: Responda EXCLUSIVAMENTE com o objeto JSON estruturado. Não coloque textos antes ou depois.`;
        }
      }

      // Ordena provedores para este agente:
      // 1º Provedor escolhido manualmente na tela do gerador (preferredProviderId)
      // 2º Provedor vinculado ao prompt (se estiver ativo e com chave)
      // 3º Provedor principal ativo do sistema
      // 4º Demais provedores com chave cadastrada (failover automático entre Google / Anthropic / OpenAI)
      const orderedProviders = [];
      if (preferredProviderId) {
        const chosenProv = allConfigRecords.find(c => c.id === preferredProviderId);
        if (chosenProv) orderedProviders.push(chosenProv);
      }
      if (agent.provedor_id) {
        const specificProv = allConfigRecords.find(c => c.id === agent.provedor_id && c.ativo);
        if (specificProv && !orderedProviders.some(c => c.id === specificProv.id)) {
          orderedProviders.push(specificProv);
        }
      }
      if (!orderedProviders.some(c => c.id === activeConfigRecord.id)) {
        orderedProviders.push(activeConfigRecord);
      }
      allConfigRecords.forEach(c => {
        if (!orderedProviders.some(existing => existing.id === c.id)) {
          orderedProviders.push(c);
        }
      });

      let stepOutput = "";
      let stepError = null;
      let stepSucceeded = false;

      for (let pIdx = 0; pIdx < orderedProviders.length; pIdx++) {
        const providerCfg = orderedProviders[pIdx];
        const providerKey = providerCfg.api_key_encrypted;
        const modelToUse = normalizeModelName(
          providerCfg.provedor,
          (pIdx === 0 ? agent.modelo_override : "") || providerCfg.modelo_override || providerCfg.modelo_padrao
        );

        try {
          if (providerCfg.provedor === "google") {
            const geminiRes = await generateGeminiContentWithFallback({
              apiKey: providerKey,
              preferredModel: modelToUse,
              promptText: `${promptToSend}\n\nEntrada a processar:\n${currentInput}`,
              temperature: temp,
              onStep: stepNotify
            });
            stepOutput = geminiRes.text;
            lastUsedProviderName = providerCfg.nome_exibicao || "Google Gemini";
            lastUsedModelName = geminiRes.modelUsed;
            stepSucceeded = true;

          } else if (providerCfg.provedor === "anthropic") {
            const anthropicRes = await generateAnthropicContentWithFallback({
              apiKey: providerKey,
              preferredModel: modelToUse,
              systemPrompt: promptToSend,
              userInput: currentInput,
              temperature: temp,
              onStep: stepNotify
            });
            stepOutput = anthropicRes.text;
            lastUsedProviderName = providerCfg.nome_exibicao || "Anthropic Claude";
            lastUsedModelName = anthropicRes.modelUsed;
            stepSucceeded = true;

          } else if (providerCfg.provedor === "openai") {
            const url = "https://api.openai.com/v1/chat/completions";
            const res = await fetchWithRetry(
              url,
              {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  "Authorization": `Bearer ${providerKey}`
                },
                body: JSON.stringify({
                  model: modelToUse,
                  temperature: temp,
                  messages: [
                    { role: "system", content: promptToSend },
                    { role: "user", content: currentInput }
                  ]
                })
              },
              2,
              1500,
              (attempt, nextDelay, status) => {
                stepNotify(`OpenAI ocupado (${status}). Re-tentando (${attempt}/2)...`);
              }
            );

            const resData = await res.json();
            stepOutput = resData.choices?.[0]?.message?.content || "";
            lastUsedProviderName = providerCfg.nome_exibicao || "OpenAI";
            lastUsedModelName = modelToUse;
            stepSucceeded = true;
          }

          if (stepSucceeded) break;
        } catch (provErr) {
          stepError = provErr;
          if (pIdx < orderedProviders.length - 1) {
            const nextProv = orderedProviders[pIdx + 1];
            console.warn(
              `[Pipeline Failover] Provedor '${providerCfg.nome_exibicao}' falhou (${provErr.message}). Alternando automaticamente para '${nextProv.nome_exibicao}'...`
            );
            stepNotify(
              `Cota/limite atingido em ${providerCfg.nome_exibicao}. Alternando automaticamente para ${nextProv.nome_exibicao}...`
            );
          }
        }
      }

      if (!stepSucceeded) {
        throw stepError || new Error("Não foi possível executar o agente de IA em nenhum provedor configurado.");
      }

      rawOutputs.push({
        agent: `${agent.nome} (${lastUsedProviderName} • ${lastUsedModelName})`,
        output: stepOutput
      });

      currentInput = stepOutput; // Encadeia para o próximo agente
    }

    stepNotify("Formatando e estruturando anúncio...");

    // 5. Parse resiliente do resultado final
    const finalRawOutput = rawOutputs[rawOutputs.length - 1]?.output || "";
    const parsedResult = extractAndParseJson(finalRawOutput, {
      sku,
      nome,
      categoria,
      modelo
    });

    return {
      result: parsedResult,
      rawOutputs,
      provedorUsado: lastUsedProviderName,
      modeloUsado: lastUsedModelName
    };
  }
};
